import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { client } from "../../../App";
import FiltersBox from "../../common/FiltersBox/FiltersBox";
import Hero from "../../common/Hero/Hero";
import ContarctListWrapper from "./ContractListWrapper/ContractListWrapper";
import SuccessModal from "../../forms/ContractAddForm/Modal/index";
import Modal from "../../common/Modal/Modal";
import EmailModal from "../../common/ConfirmEmailModal/EmailModal";
import NoResult from "../../../components/common/NoResult/NoResult";
import { LIST_CONTRACTS } from "../../../helpers/queries";
import {
  handlerErrors,
  isObject,
  formatContractStatus,
  SwitchUser,
  checkConractStatus,
} from "../../../helpers/helpers";
import { hideLoading, showLoading } from "../../../reducers/app";
import { enableBills, disableBills, changeEmailBills } from "./helpers";
import "./ContractsPage.scss";
import { UserRoleEnum } from "../../../helpers/enums";
import { setCurrentContract } from "../../../reducers/contracts";

const ContractsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const auth = useSelector((state) => state.auth);
  const userProfile = SwitchUser(auth);
  const role = userProfile.role;
  const { t } = useTranslation();
  // eslint-disable-next-line
  const [, setFilters] = useState({});
  const [contracts, setContracts] = useState([]);
  const [clickedRowIndex, setClickedRow] = useState(null);
  const [genericError, setGenericError] = useState(null);
  const [infoModal, setInfoModal] = useState({ active: false, type: null });
  const [refresh, setRefresh] = useState(false);

  const contract = useSelector((state) => state.contracts.current);
  const listContractsState = useSelector((state) => state.contracts.list);

  // Filtering useState tuple
  const [filteredContracts, setFilteredContracts] = useState(contracts);
  const filterConfig = {
    fields: [
      {
        label:  t('contracts.filter.header'),
        name: "search_key",
        type: "input",
        placeholder: t('header.menu.search'),
        columnSize: "5"
      },
    ],
    validations: [],
    configuration: {
      hasReset: true,
    },
  };
  const onFiltersChanged = (e) => {
    setFilters(e);
  };

  const listContracts = async () =>
    client().query({
      query: LIST_CONTRACTS,
    });

  const tutelaControl = useCallback((status) => {
    if (status) {
      setInfoModal({ active: true, type: "DISABLE_TUTELA" });
    } else {
      history.push("/tutela-perdite")
    }
  }, [history, setInfoModal])

  const columns = React.useMemo(
    () => [
      {
        // Make an expander cell
        Header: () => null,

        id: "checkBox", // It needs an ID
        accessor: "expander",
        Cell: ({ row, getToggleAllRowsSelectedProps }) => {
        const p = row.getToggleRowSelectedProps();
        delete p.indeterminate;
        delete p.title;
        return (
            <label
              className="container-checkbox"
              htmlFor={`radio-${row.id}`}
              aria-checked={p.checked}
            >
              <input
                {...p}
                type="radio"
                name="selectedContract"
                id={`radio-${row.id}`}
                onChange={(e) => {
                  p.onChange(e);
                  setClickedRow(row.id);
                }}
                 aria-label={t("select.contract.table") + " " + row.original.codice_contratto}
                 
              />
              <span className="checkmark checkmark-radio" />
            </label>
          );
        }
      },
      {
        Header: t("contract.visualization.contract.code"),
        accessor: "codice_contratto",
        id: 1,
      },
      {
        Header: t("contract.visualization.user.code"),
        accessor: "codice_utenza",
        id: 2,
      },
      {
        Header: t("contract.visualization.owner"),
        accessor: "intestatario",
        id: 3,
      },
      {
        Header: t("contract.visualization.point.supply"),
        accessor: "punto_fornitura",
        id: 4,
      },
      {
        Header: t("contract.visualization.online.bill"),
        accessor: "bolletta_online",
        id: 5,
      },

      {
        Header: t("contract.visualization.contract.status"),
        accessor: "stato_contratto",
        id: 6,
      },
      {
        Header: "serviceBankDomiciliation",
        accessor: "serviceBankDomiciliation",
        id: 7,
      },
      {
        Header: "serviceLoanLossProvisions",
        accessor: "serviceLoanLossProvisions",
        id: 8,
      },
      {
        // Build our expander column
        Header: () => null,
        id: "expander",
        Cell: ({ row }) => {
          const props = row.getToggleRowExpandedProps();
          delete props.title; 

          return (
            <button
              type="button"
              id={`expand-button-${row.id}`}
              {...props} 
              aria-expanded={row.isExpanded}
              aria-controls={`accordion-panel-${row.id}`}
              aria-label={row.isExpanded ? t("close.accordion.label") : t("expander.accordion.label")}
              className="expand-button"
            >
              {row.isExpanded ? (
                <span className="gc-icon-functional-chevron-up" aria-hidden="true" />
              ) : (
                <span className="gc-icon-functional-chevron-down" aria-hidden="true" />
              )}
            </button>
          );
        }
      }
    ],
    [t]
  );

  const renderRowSubComponent = React.useCallback(
    ({ row }) => {
      const serviceBankDomiciliation = row.values["7"];
      const serviceLoanLossProvisions = row.values["8"];
      return (
        <div className="table-sub-row">
          <div className="inner-container">
            <div className="loan-loass-prevision">
              <p>{t("contract.serviceLoanLossProvisions")}</p>

              {checkConractStatus(contract.state) ? (
                serviceLoanLossProvisions ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  tutelaControl(true);
                }}
                className="underline link-style"
              >
                {t("contract.disable")}
              </button>
                ) : (
                  <a
                    href="/tutela-perdite"
                    className="underline link-style"
                    onClick={(e) => {
                      e.preventDefault();
                      tutelaControl(false);
                    }}
                  >
                    {t("common.active")}
                  </a>
                )
              ) : (
                <p className="empty">{"-"}</p>
              )}
            </div>
            <div className="service-bank-domiciliation">
              <p>{t("contract.serviceBankDomiciliation")}</p>
              <div className="d-flex">
              {checkConractStatus(contract.state) ? (
                serviceBankDomiciliation ? (
                  <>
                    <a
                      href="/domiciliazione-bancaria"
                      className="underline domiciliation-active-button link-style"
                      onClick={(e) => {
                        e.preventDefault();
                        history.push("/domiciliazione-bancaria");
                      }}
                    >
                      {t("contract.disable")}
                    </a>
                    <a
                      href="/modifica-domiciliazione-bancaria"
                      className="underline domiciliation-modify-button link-style"
                      onClick={(e) => {
                        e.preventDefault();
                        const contractId = row?.original?.contractId;
                        let selectedContract = listContractsState.find(
                          (contract) => contract.id === contractId
                        );
                        dispatch(setCurrentContract(selectedContract)).then(() =>
                          history.push("/modifica-domiciliazione-bancaria")
                        );
                      }}
                    >
                      {t("contract.serviceBankDomiciliation.modification")}
                    </a>
                  </>
                ) : (
                  <a
                    href="/domiciliazione-bancaria"
                    className="underline link-style"
                    onClick={(e) => {
                      e.preventDefault();
                      window.dataLayer.push({ event: "domiciliazioneBancariaIniziata" });
                      history.push("/domiciliazione-bancaria");
                    }}
                  >
                    {t("common.active")}
                  </a>
                )
              ) : (
                <p className="empty">{"-"}</p>
              )}
              </div>
            </div>
          </div>
        </div>
      );
    },
    [t, history, contract, tutelaControl]
  );

  const enableBillsHandler = (email) => {
    setGenericError(null);
    const contractId = infoModal.id;
    enableBills(
      contractId,
      email,
      dispatch,
      setGenericError,
      setInfoModal,
      null,
      setRefresh,
      refresh
    );
  };

  const disableBillsHandler = () => {
    setGenericError(null);
    const contractId = infoModal.id;
    disableBills(
      contractId,
      dispatch,
      setGenericError,
      setInfoModal,
      null,
      setRefresh,
      refresh
    );
  };

  const changeEmailBillsHandler = (email) => {
    setGenericError(null);
    const contractId = infoModal.id;
    changeEmailBills(
      contractId,
      email,
      dispatch,
      setGenericError,
      setInfoModal,
      setRefresh,
      refresh
    );
  };

  const orderColumns = ["test1", "test2", "test3"];

  const onlineBillFormater = (isActive, id, email) => {
    if (checkConractStatus(contract.state)) {
      if (isActive) {
        return (
        <button
          type="button"
          onClick={() => setInfoModal({ active: true, type: "DISABLE", id, email })}
          className="underline link-style"
        >
          {t("contract.deactivate.or.modify")}
        </button>
        );
      } else {
        return (
          <button
            type="button"
            onClick={() => {
              window.dataLayer.push({ 'event': 'attivazioneBollettaStep1' });
              setInfoModal({ active: true, type: "ENABLE", id, email });
            }
            }
            className="underline link-style"
          >
            {t("contract.active")}
          </button>
        );
      }
    } else {
      if (isActive) {
        return <p className="underline-text">{t("contract.deactivate.or.modify")}</p>;
      } else {
        return <p className="empty">{"-"}</p>;
      }
    }
  };

  const handleGenericError = () => {
    console.log("AAA", genericError)
    let error = isObject(genericError)
      ? genericError[Object.keys(genericError)[0]]
      : genericError;
    let formatedError = error.includes("_")
      ? t(`associate-contact.${error}`) : t(`${error}`);


    return (
      <div className={"row"}>
        <div className={"container"}>
          <div className="col-sm-12 mt-5">
            <div className={"generic-error d-flex justify-content-start"}>
              <i
                className="gc-icon-functional-attention-1-col"
                aria-hidden="true"
              ></i>
              <p>{formatedError}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    dispatch(showLoading());
    listContracts()
      .then(({ data }) => {
        if (data.listContracts.pagination.totalElements) {
          const formated = data.listContracts.elements.map(
            ({
              contractId,
              integraContractCode,
              integraClientCode,
              accountHolder,
              puf,
              serviceOnlineBill,
              status,
              serviceBankDomiciliation,
              serviceLoanLossProvisions,
            }) => {
              return {
                contractId,
                codice_contratto: integraContractCode,
                codice_utenza: integraClientCode,
                intestatario: accountHolder.businessName,
                punto_fornitura: `${puf.street}, ${puf.streetNumber}, ${puf.city}`,
                bolletta_online: onlineBillFormater(
                  serviceOnlineBill.enabled,
                  contractId,
                  serviceOnlineBill.email
                ),
                stato_contratto: formatContractStatus(status.code),
                serviceOnlineBill: serviceOnlineBill.enabled,
                serviceBankDomiciliation: serviceBankDomiciliation.enabled,
                serviceLoanLossProvisions: serviceLoanLossProvisions.enabled,
              };
            }
          );
          setContracts(formated);
        }
        dispatch(hideLoading());
      })
      .catch((err) => {
        dispatch(hideLoading());
        handlerErrors(err, setGenericError, setGenericError);
      });
    // eslint-disable-next-line
  }, [refresh]);

  const renderInfoModal = () => {
    const email = infoModal.email;
    switch (infoModal.type) {
      case "DISABLE": {
        return (
          <EmailModal
            email={email}
            title={t("email.modal.title")}
            emailTitle={t("email.modal.header")}
            emailPlaceholder={t("email.modal.placeholder")}
            btnText={t("email.modal.btnDeactivate")}
            btnConfirm={t("email.moda.btnConfirm")}
            isOpen={true}
            disable={disableBillsHandler}
            changeEmailBill={changeEmailBillsHandler}
            onClose={() => setInfoModal({ active: false, type: null })}
            to={"/"}
            desc={contracts.filter(i => i.contractId === infoModal.id)[0].serviceLoanLossProvisions && contracts.filter(i => i.contractId === infoModal.id)[0].serviceOnlineBill ? 'Disattivando la bolletta online la tutela perdite diventerà a pagamento dal 1 gennaio del prossimo anno.' : null}
            // info={t("add_online_bills_popup")}
            info={""}
          />
        );
      }

      case "ENABLE": {
        return (
          <EmailModal
            title={t("email.modal.title")}
            emailTitle={t("email.modal.header")}
            emailPlaceholder={t("email.modal.placeholder")}
            btnText={t("email.modal.btnText")}
            isOpen={true}
            enable={enableBillsHandler}
            onClose={() => setInfoModal({ active: false, type: null })}
            to={"/"}
            // info={t("add_online_bills_popup")}
            info={""}
          />
        );
      }
      case "DISABLE_TUTELA": {
        return (
          <Modal
            descriptionClass={"active-section"}
            description={
              <><span className="sp-text">
                {t("contract.disableTutela.part1")}
              </span>
              <span className="sp-text-block">
                {t("contract.disableTutela.part2")}
              </span>
              <span className="sp-text">
                {t("contract.disableTutela.email")}
              </span>
              <span className="sp-text-block">
                {t("contract.disableTutela.part3")}
              </span></>
            }
            type={"button"}
            show={true}
            onClose={() => setInfoModal({ active: false, type: null })}
            onFirstClick={() => setInfoModal({ active: false, type: null })}
            linkTitle={t("common.hoCapito")}
          />
        );
      }
      case "ACTIVE_TUTELA": {
        return (
          <div className="no-close">
            <Modal
              content={
                <div className="disactive-section paragraph-main-fs8">
                  <span className="sp-text">
                    {t("contract.activeTutela.part1")}
                    <span className="sp-text-link">{t("contract.email")}</span>
                    <span className="sp-text">
                      {t("contract.activeTutela.part2")}
                    </span>
                  </span>

                  <div className="text-section-2">
                    <span className="sp-text">
                      {t("contract.activeTutela.part3")}
                      <span className="sp-text-link">
                        {t("contract.activeTutela.link")}
                      </span>
                    </span>
                  </div>
                </div>
              }
              show={true}
              onClose={() => setInfoModal({ active: false, type: null })}
              type={"button"}
              onFirstClick={() => setInfoModal({ active: false, type: null })}
              linkTitle={t("common.hoCapito")}
            />
          </div>
        );
      }
      default: {
        return null;
      }
    }
  };

  // const renderConfirmModal = () => {
  //   return (
  //     <SuccessModal
  //       desc={
  //         <div>
  //           <p>
  //           {!viewDisabledMessage
  //             ? t("confirm.online.bill.description")
  //             : t("confirm.online.bill.description.disable")
  //           }
  //             </p>
  //           <div className="btn-container">
  //             <Link
  //               onClick={() => setActive(false)}
  //               className={"btn btn-primary"}
  //               title="Vai a "
  //               to={"/contracts"}
  //             >
  //               {t("common.hoCapito")}
  //             </Link>
  //           </div>
  //         </div>
  //       }
  //       icon={"gc-icon-functional-check-2-col"}
  //       isOpen={true}
  //       to={"close"}
  //       onClose={() => setActive(false)}
  //     />
  //   );
  // };

  return (
    <>
      <div className="ContractsPage" data-testid="ContractsPage">
        <Hero
          helpText={t("contact.page.modal.title")}
          backTo={"/"}
          backToText={t("common.back.home")}
          title={t("contact.header")}
          description={
            role !== UserRoleEnum.MUNICIPAL_USER ? (
              <div>
                <p>{t("contact.title")}</p>
                <Link to={"/contracts/add"}>{t("contact.link")}</Link>
              </div>
            ) : (
                <div>{ }</div>
              )
          }
        />
        <FiltersBox
          config={filterConfig}
          onChange={onFiltersChanged}
          data={contracts}
          onDataFiltered={setFilteredContracts}
          filter={(contracts, filters) => {
            if (Object.keys(filters).length === 0) {
              return contracts;
            }
            return contracts.filter(
              (contract) =>
                contract.codice_contratto
                  .toLowerCase()
                  .includes(filters.search_key.toLowerCase()) ||
                contract.intestatario
                  .toLowerCase()
                  .includes(filters.search_key.toLowerCase())
            );
          }}
        />
        {filteredContracts.length > 0 ? (
          <ContarctListWrapper
            clickedRowIndex={clickedRowIndex}
            columns={columns}
            data={filteredContracts}
            renderRowSubComponent={renderRowSubComponent}
            orderColumns={orderColumns}
            setClickedRow={setClickedRow}
            isDisablePage={false}
            disableContracts={(contracts) =>
              console.log("contracts", contracts)
            }
            tableName={t("page_titles.page.contracts")}
            t={t}
            role={role}
          />
        ) : (
            <NoResult text={t("contracts.noResults")} />
          )}
        {genericError && handleGenericError()}

        {infoModal.active && renderInfoModal()}

        {/* {active && renderConfirmModal()} */}
      </div>
    </>
  );
};

ContractsPage.propTypes = {};

export default ContractsPage;
